/*
 * Client hooks to resolve the "active" categories of the storefront navigation using
 * the app_gdi custom API `navigation/active-categories`.
 *
 * The backend returns the FULL flat list of active category ids (all levels) that
 * have online, searchable (and, when the `navigationOrderableProductsOnlyEnabled`
 * site preference is on, orderable) products assigned. One call for the root category
 * therefore filters the entire navigation, so the whole nav shares a single cached
 * request. Filtering is gated on the preference: when it is off, no call is made and
 * the nav is left unfiltered (exactly like before this feature).
 */

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../utils/site-config'
import {getRootCategoryId} from '../utils/brandUtils'
import {useBrand} from '../contexts/brand-context'

// MRT exposes this site preference in app config as an env-like key.
const ORDERABLE_ONLY_ENV_VAR = 'NAVIGATION_ORDERABLE_PRODUCTS_ONLY_ENABLED'

// Cache active-category lists for an hour to match the default `navigationCache`
// site preference and keep the number of custom-API calls low.
const ACTIVE_CATEGORIES_STALE_TIME = 60 * 60 * 1000

const fetchActiveCategories = async ({
    appOrigin,
    proxyPath,
    organizationId,
    siteId,
    locale,
    categoryId,
    token
}) => {
    let url =
        `${appOrigin}${proxyPath}/custom/navigation/v1/organizations/${organizationId}` +
        `/active-categories?siteId=${encodeURIComponent(siteId)}` +
        `&c_category=${encodeURIComponent(categoryId)}`
    if (locale) {
        url += `&locale=${encodeURIComponent(locale)}`
    }

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    })

    if (!response.ok) {
        throw new Error(`Failed to fetch active categories: ${response.status}`)
    }

    return response.json()
}

// Shared request context (origin, token, org/site/locale) for the queries below.
const useActiveCategoriesContext = () => {
    const appOrigin = useSsrFetchOrigin()
    const {getTokenWhenReady} = useAccessToken() || {}
    const {site, locale} = useMultiSite() || {}
    const config = getConfig()
    const commerceAPI = config?.app?.commerceAPI || {}
    const parameters = commerceAPI.parameters || {}
    return {
        appOrigin,
        getTokenWhenReady,
        proxyPath: commerceAPI.proxyPath,
        organizationId: parameters.organizationId,
        siteId: site?.id || parameters.siteId,
        localeId: locale?.id
    }
}

// Builds a react-query options object for a single category's active children.
const buildActiveCategoriesQuery = (ctx, categoryId, enabled = true) => {
    const {appOrigin, getTokenWhenReady, proxyPath, organizationId, siteId, localeId} = ctx
    return {
        queryKey: ['activeCategories', organizationId, siteId, localeId, categoryId],
        enabled: enabled && !!organizationId && !!siteId && !!categoryId,
        queryFn: async () => {
            const token = await getTokenWhenReady()
            return fetchActiveCategories({
                appOrigin,
                proxyPath,
                organizationId,
                siteId,
                locale: localeId,
                categoryId,
                token
            })
        },
        staleTime: ACTIVE_CATEGORIES_STALE_TIME,
        refetchOnWindowFocus: false,
        retry: 1
    }
}

// Converts a raw API response into a Set of active IDs, or null when unavailable.
const toActiveSet = (data) =>
    data && data.success === true && Array.isArray(data.categories)
        ? new Set(data.categories)
        : null

// True only when the orderable-only switch is on in MRT app config. Used to
// gate every active-categories request so turning it off restores the original
// (unfiltered) navigation with zero calls to the custom API.
const useNavigationFilteringEnabled = () => {
    const {site} = useMultiSite() || {}
    const {app: appConfig} = getConfig() || {}
    return getSiteEnvVarValue(appConfig, ORDERABLE_ONLY_ENV_VAR, site?.id) === 'true'
}

/**
 * Resolves the set of active child-category IDs for `categoryId`.
 *
 * @param {string} categoryId - The parent category whose active children are requested.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - Disable the request (e.g. maintenance mode).
 * @returns {{activeCategoryIds: (Set<string>|null), isLoading: boolean, filteringEnabled: boolean}}
 *   `activeCategoryIds` is a Set of the active child IDs, or `null` when the list
 *   is unavailable (loading, disabled, backend error) — callers must treat `null`
 *   as "do not filter" so the navigation never disappears on a failed lookup.
 */
export const useActiveCategories = (categoryId, {enabled = true} = {}) => {
    const ctx = useActiveCategoriesContext()
    const filteringEnabled = useNavigationFilteringEnabled()
    const canRun =
        enabled && filteringEnabled && !!ctx.organizationId && !!ctx.siteId && !!categoryId
    const {data, isLoading} = useQuery(
        buildActiveCategoriesQuery(ctx, categoryId, enabled && filteringEnabled)
    )
    return {
        activeCategoryIds: toActiveSet(data),
        // Only report loading while a real request is in flight. A disabled query
        // reports isLoading=true in react-query, which must not hold the nav.
        isLoading: canRun && isLoading && !data,
        filteringEnabled
    }
}

/**
 * Returns a shallow clone of `node` whose immediate `itemsKey` children are limited
 * to those present in `activeCategoryIds`. When `activeCategoryIds` is `null`
 * (list unavailable) the node is returned unchanged so the navigation fails open.
 *
 * @param {object} node - A category node holding an `itemsKey` array of children.
 * @param {(Set<string>|null)} activeCategoryIds - Allowed child IDs, or null to skip filtering.
 * @param {string} [itemsKey='categories'] - The property holding the child array.
 * @returns {object} The node (cloned when filtering is applied).
 */
export const filterActiveChildren = (node, activeCategoryIds, itemsKey = 'categories') => {
    if (!node || !activeCategoryIds || !Array.isArray(node[itemsKey])) {
        return node
    }
    return {
        ...node,
        [itemsKey]: node[itemsKey].filter((child) => child && activeCategoryIds.has(child.id))
    }
}

// Recursively rebuilds `node`, filtering EVERY level's children against the single
// flat set of active category ids returned for the root. Fail open when null.
const pruneTreeByActiveSet = (node, itemsKey, activeCategoryIds) => {
    if (
        !node ||
        !activeCategoryIds ||
        !Array.isArray(node[itemsKey]) ||
        node[itemsKey].length === 0
    ) {
        return node
    }
    return {
        ...node,
        [itemsKey]: node[itemsKey]
            .filter((child) => child && activeCategoryIds.has(child.id))
            .map((child) => pruneTreeByActiveSet(child, itemsKey, activeCategoryIds))
    }
}

/**
 * Prunes an already-fetched category subtree so only active categories remain at
 * every level. Reuses the single root active-categories lookup (the backend returns
 * the full active descendant set), so it adds NO extra network calls — it shares the
 * same cached query as the top-level nav.
 *
 * @param {object} node - The fetched category subtree (root of what is shown).
 * @param {string} [itemsKey='categories'] - Property holding the child array.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - Disable the lookup.
 * @returns {{prunedNode: object, isLoading: boolean, filteringEnabled: boolean}}
 *   `prunedNode` equals `node` until the lookup resolves (fail open). `isLoading`
 *   is true only while a real lookup is in flight (never when the feature is off).
 */
export const useActiveCategoriesTree = (node, itemsKey = 'categories', {enabled = true} = {}) => {
    const {site} = useMultiSite() || {}
    const {brandId} = useBrand() || {}
    const rootCategoryId = getRootCategoryId(site?.id, brandId)
    const {activeCategoryIds, isLoading, filteringEnabled} = useActiveCategories(rootCategoryId, {
        enabled
    })

    const prunedNode = useMemo(
        () => pruneTreeByActiveSet(node, itemsKey, activeCategoryIds),
        [node, itemsKey, activeCategoryIds]
    )

    return {prunedNode, isLoading, filteringEnabled}
}
